import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { Box, Button, CircularProgress, Typography, makeStyles } from "@material-ui/core";
import services from "../../services";
import IFrame from "../../components/IFrame";
import { useStores } from "../../hooks/use-stores";

const VAULTED_LOGIN_URL = "https://secure.vaulted.com/login";
const VAULTED_SIGNUP_URL = "https://secure.vaulted.com/signup";

const FRAMER_EN_URL = "https://mystarfishperks.framer.website/en/royal/order-gold";
const FRAMER_FR_URL = "https://mystarfishperks.framer.website/fr/royal/order-gold-fr";

const useRedirectStyles = makeStyles((theme) => ({
  root: {
    minHeight: "60vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(2),
    color: "#fff",
    padding: theme.spacing(3),
  },
}));

function compactDateForVaulted(value) {
  if (!value) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    return trimmed;
  }
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = String(d.getFullYear());
    return `${mm}/${dd}/${yyyy}`;
  } catch {
    return null;
  }
}

function buildVaultedSignupUrl(profile) {
  if (!profile) return VAULTED_SIGNUP_URL;
  const address = profile.address || {};
  const params = {
    affiliatedCompany: "LIFE",
    email: profile.email,
    firstName: profile.firstName,
    lastName: profile.lastName,
    phoneNumber: profile.phone ?? profile.phoneNumber,
    birthday: compactDateForVaulted(
      profile.birthday ?? profile.birthDate ?? profile.dateOfBirth,
    ),
    addressLine1: address.address1 ?? address.line1 ?? address.addressLine1,
    addressLine2: address.address2 ?? address.line2 ?? address.addressLine2,
    city: address.city,
    state: address.region ?? address.state,
    zip: address.zip ?? address.postalCode,
    customerId: profile.userID ?? profile.customerId,
  };

  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === null || v === undefined) return;
    const s = String(v).trim();
    if (!s) return;
    qs.set(k, s);
  });
  const query = qs.toString();
  return query ? `${VAULTED_SIGNUP_URL}?${query}` : VAULTED_SIGNUP_URL;
}

async function resolveVaultedUrl() {
  const [profileResult, vaultedResult] = await Promise.all([
    services.UserProfile.list(),
    services.UserProfile.getVaultedAccountStatus(),
  ]);
  if (vaultedResult?.hasVaultedAccount) {
    return VAULTED_LOGIN_URL;
  }
  return buildVaultedSignupUrl(profileResult);
}

function goToVaulted(url) {
  window.location.replace(url);
}

export const OrderGoldVaultedRedirect = observer(function OrderGoldVaultedRedirect() {
  const classes = useRedirectStyles();
  const { authStore } = useStores();
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = await resolveVaultedUrl();
        if (cancelled) return;
        if (!authStore.isAuthenticated || !authStore.token) return;
        goToVaulted(url);
      } catch {
        if (cancelled) return;
        if (!authStore.isAuthenticated || !authStore.token) return;
        setError("We could not open Vaulted. Please try again.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authStore.isAuthenticated, authStore.token]);

  return (
    <Box className={classes.root}>
      {error ? (
        <>
          <Typography align="center">{error}</Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => window.location.reload()}
          >
            Try again
          </Button>
        </>
      ) : (
        <CircularProgress size={48} />
      )}
    </Box>
  );
});

function OrderGold() {
  const { commonStore } = useStores();
  const { language } = commonStore;

  const framerUrl = useMemo(() => {
    const lang = String(language || "en").toLowerCase();
    return lang.startsWith("fr") ? FRAMER_FR_URL : FRAMER_EN_URL;
  }, [language]);

  return <IFrame url={framerUrl} />;
}

export default observer(OrderGold);
